import clsx from 'clsx';
import React from 'react';

import { useAppContext } from '@contexts/AppContext';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { topic_landing_path } from '@routes';
import { getImgxSrc } from '@utils/images';

import { ArticleTimeLineEvent } from './timeLineEvents';

interface VerticalTimePointProps {
  articles: ArticleTimeLineEvent;
}

const VerticalTimePoint = ({ articles }: VerticalTimePointProps) => {
  const hydrated = useServerSideRendering();
  const { env } = useAppContext();
  const tiArticle = articles[0];
  const externalArticle = articles[1];

  return (
    <>
      {hydrated && (
        <>
          <a
            className="topic-badge track-link absolute left-12 top-0"
            data-type="link"
            data-name={`topic badge: ${tiArticle.topic}`}
            data-location="brand_campaign#index"
            href={topic_landing_path({ topic: tiArticle.topic })}
          >
            {tiArticle.topic}
          </a>
          <div className="flex">
            <VerticalTimePointEvent eventType="ti">
              {tiArticle.month} <span className="block sm:inline">{tiArticle.year}</span>
            </VerticalTimePointEvent>
            <img
              className="absolute left-16 top-40 ml-px h-72 w-20 rotate-90 object-cover object-bottom sm:top-72"
              src={getImgxSrc({
                path: 'brand_campaign/pinRed.svg',
                isDevDependence: true,
                envContext: env,
              })}
              alt="TI Event"
            />
            <div className="absolute left-12 top-80 mt-4 pt-4 sm:left-72 sm:ml-24 sm:mt-0">
              <img
                className="max-w-[180px] sm:max-w-[265px]"
                src={getImgxSrc({
                  path: tiArticle.logoPath,
                  isDevDependence: true,
                  envContext: env,
                })}
                alt="TI Logo"
              />
              <div className="w-full max-w-[260px] sm:max-w-[350px]">
                <a
                  className="text-left font-serif text-button-lg leading-6 tracking-normal underline sm:text-[22px] sm:leading-8"
                  href={tiArticle.articleLink}
                >
                  “{tiArticle.articleTitle}”
                </a>
              </div>
            </div>
          </div>
          <div className="flex">
            <VerticalTimePointEvent eventType="competitor">
              {externalArticle.month}{' '}
              <span className="block sm:inline">{externalArticle.year}</span>
            </VerticalTimePointEvent>
            <img
              className="absolute bottom-80 left-16 ml-px h-72 w-20 rotate-90 object-cover object-bottom sm:bottom-40"
              src={getImgxSrc({
                path: 'brand_campaign/pinGray.svg',
                isDevDependence: true,
                envContext: env,
              })}
              alt="Non-TI Event"
            />
          </div>
          <div className="absolute -bottom-6 left-12 h-[108px] sm:-bottom-8 sm:left-72 sm:ml-24 sm:h-96">
            <img
              className="max-w-[150px]"
              src={getImgxSrc({
                path: externalArticle.logoPath,
                isDevDependence: true,
                envContext: env,
              })}
              alt="Competitor Logo"
            />
            <div className="w-full max-w-[260px] font-serif text-body-sm leading-6 sm:max-w-[350px] sm:text-button-lg sm:leading-6 sm:tracking-normal">
              “{externalArticle.articleTitle}”
            </div>
          </div>
        </>
      )}
    </>
  );
};

interface VerticalTimePointEventProps {
  eventType: 'ti' | 'competitor';
  children: React.ReactNode;
}

const VerticalTimePointEvent = ({ eventType, children }: VerticalTimePointEventProps) => {
  return (
    <div
      className={clsx(
        'absolute -left-64 w-48 text-right text-[8px] font-bold uppercase tracking-widest sm:-left-[10rem] sm:w-[138px] sm:max-w-full sm:text-overline',
        {
          'top-64 text-pro-red sm:top-112 sm:-mt-12': eventType === 'ti',
          'bottom-112 -mb-8 sm:bottom-[4.25rem] sm:mb-0': eventType === 'competitor',
        }
      )}
    >
      {children}
    </div>
  );
};

export default VerticalTimePoint;
